import { useGetAsset } from 'ClientPortal/src/api/Queries/assets'
import { useGetValuations } from 'ClientPortal/src/api/Queries/valuations'
import { useGetAssetEnergySummary } from 'ClientPortal/src/api/Queries/value-overview'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'
import { useTypedParams } from 'ClientPortal/src/router/useTypedParams'
import { assembleAssetTitle, isoDateStringToLocalizedMonthYear } from 'ComponentLibrary'
import dayjs from 'dayjs'
import { entries, groupBy, keyBy, sortBy } from 'lodash'
import { useTranslation } from 'react-i18next'

export function useDeviationAnalysisComponent() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const featureFlags = useFlags()

    const { assetId } = useTypedParams(['assetId'])
    const { valuations, isValuationsPending } = useGetValuations({ assetId, includeDrafts: true, filterFinalized: true })
    const { asset, isAssetPending } = useGetAsset({ assetId })
    const { assetEnergySummary, isAssetEnergySummaryLoading } = useGetAssetEnergySummary({
        assetId,
        enabled: featureFlags.isEnergySummaryEnabled,
    })

    const unsortedValuationOptions = valuations
        .filter((valuation) => valuation.valuation_type !== 'historical')
        .map((valuationItem) => {
            const valuationMonthAndYear = isoDateStringToLocalizedMonthYear(valuationItem.valuation_date, 'numeric', locale)

            return {
                value: valuationItem.uuid,
                orderId: valuationItem.order_uuid,
                groupTitle: `${tCommon('valuationReport')} ${valuationMonthAndYear}`,
                title: valuationItem.is_draft
                    ? valuationItem.title?.split(' - ')[0]
                    : `${tCommon('valuationReport')} ${valuationMonthAndYear}`,
                valuationVersion: valuationItem.is_draft ? valuationItem.title?.split(' - ')[0] : tCommon('final'),
                date: valuationItem.is_draft ? valuationItem.created_on : valuationItem.valuation_date,
                valuationDate: valuationItem.valuation_date,
                status: valuationItem.status,
                isDraft: valuationItem.is_draft,
            }
        })

    const valuationOptions = sortBy(unsortedValuationOptions, (option) => dayjs(option.date).valueOf()).toReversed()

    const showDeviationAnalysis = valuationOptions.length > 1

    const optionsByTitle = groupBy(valuationOptions, 'orderId')
    const groupedOptions = entries(optionsByTitle).map(([_, options]) => ({
        label: options[0].groupTitle,
        options: sortBy(options, (option) => dayjs(option.date).valueOf()).toReversed(),
    }))
    const sortedGroupedOptions = sortBy(groupedOptions, (option) => dayjs(option.options[0].valuationDate).valueOf()).toReversed()
    const groupedOptionsByLabel = keyBy(sortedGroupedOptions, 'label')

    const hasGroupedValuations = groupedOptions.some((valuationGroup) => valuationGroup.options.length > 1)

    return {
        assetId,
        asset,
        isValuationsPending,
        isAssetPending,
        valuationOptions,
        showDeviationAnalysis,
        groupedOptionsByLabel,
        assetTitle: assembleAssetTitle({
            ...asset?.address,
            customer_asset_reference: asset?.customer_asset_reference,
        }),
        hasGroupedValuations,
        isEnergySummaryEnabled: featureFlags.isEnergySummaryEnabled,
        assetEnergySummary,
        isAssetEnergySummaryLoading,
    }
}
